import type { DataGridStatus } from "@design-system/DataGrid";
import type { NestedTaskRow } from "../types";

const LOADING_STATUS: DataGridStatus = { state: "loading" };
const READY_STATUS: DataGridStatus = { state: "ready" };
const REFETCHING_STATUS: DataGridStatus = {
    state: "ready",
    isRefetching: true,
};

export const getTaskListStatus = ({
    isLoading,
    isRefetching,
    loadError,
}: {
    isLoading: boolean;
    isRefetching: boolean;
    loadError?: { onRetry: () => void };
}): DataGridStatus => {
    if (loadError) {
        return { state: "error", onRetry: loadError.onRetry };
    }
    if (isLoading) {
        return LOADING_STATUS;
    }
    return isRefetching ? REFETCHING_STATUS : READY_STATUS;
};

const NO_SUBTASKS: NestedTaskRow["tasks"] = [];

type GroupTask = NestedTaskRow["tasks"][number];

const taskRowsByTask = new WeakMap<GroupTask, NestedTaskRow>();

/**
 * One row object per task object: groups are rebuilt on every task change,
 * and DataGrid redraws a row only when its `row.original` changes.
 */
const toTaskRow = (task: GroupTask) => {
    const cached = taskRowsByTask.get(task);
    if (cached) {
        return cached;
    }
    const taskRow = { ...task, tasks: NO_SUBTASKS };
    taskRowsByTask.set(task, taskRow);
    return taskRow;
};

export const getTaskRows = (row: NestedTaskRow) =>
    row.isGroup ? row.tasks.map(toTaskRow) : undefined;

export const getTaskRowId = (row: NestedTaskRow) => row.id;

export const isGroupRow = (row: NestedTaskRow) => Boolean(row.isGroup);

/**
 * Expand all as each group with tasks: the toolbar's toggle and
 * `useTaskGroupsExpansion` read expansion as a map of group ids, not `true`.
 */
export const toExpandedGroups = (groups: readonly NestedTaskRow[]) =>
    Object.fromEntries(
        groups
            .filter((group) => group.tasks.length > 0)
            .map((group) => [group.id, true]),
    );
